import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import ShareRow from '../../components/ShareRow/ShareRow';
import { SIGNS, SIGN_BY_SLUG } from '../../data/astro/signs';
import { SIGN_PROFILES, pairPath, rankedMatches } from '../../data/compatibility';
import { Faq, ScoreBadge, breadcrumbs, faqJsonLd } from './parts';
import '../Astro/Astro.css';
import './Compatibility.css';

const PickList = ({ title, items }) => (
  <div className="compat-picks">
    <h3>{title}</h3>
    <ol>
      {items.map((item) => (
        <li key={item.sign}>
          <strong>{SIGN_BY_SLUG[item.sign].glyph} {SIGN_BY_SLUG[item.sign].name}</strong>
          <span>{item.why}</span>
        </li>
      ))}
    </ol>
  </div>
);

const CompatibilitySign = ({ sign }) => {
  const info = SIGN_BY_SLUG[sign];
  const profile = SIGN_PROFILES[sign];
  const ranked = rankedMatches(sign);
  const path = `/zodiac-compatibility/${sign}/`;
  const description = profile?.meta_description
    || `${info.name} compatibility in love: the best and hardest matches for ${info.name}, with scores and a reading for every sign.`;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        headline: `${info.name} Compatibility: Best and Worst Matches in Love`,
        description,
        url: `https://nowtarot.com${path}`,
        publisher: { '@type': 'Organization', name: 'NowTarot', url: 'https://nowtarot.com/' },
      },
      breadcrumbs([['Zodiac compatibility', '/zodiac-compatibility/'], [`${info.name} compatibility`, path]]),
      ...(profile?.faq ? [faqJsonLd(profile.faq)] : []),
    ],
  };

  return (
    <div className="astro-page compat-page">
      <Helmet>
        <title>{`${info.name} Compatibility: Best and Worst Love Matches | NowTarot`}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={`https://nowtarot.com${path}`} />
        <meta property="og:title" content={`${info.name} Compatibility: Best and Worst Love Matches`} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={`https://nowtarot.com${path}`} />
        <meta property="og:image" content="https://nowtarot.com/og-image.png" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>
      <Header />
      <main>
        <nav className="compat-crumbs" aria-label="Breadcrumb">
          <Link to="/zodiac-compatibility/">Zodiac compatibility</Link> <span aria-hidden="true">/</span> {info.name}
        </nav>
        <p className="astro-eyebrow">{info.dates} · {info.element} · {info.modality} · ruled by {info.ruler}</p>
        <h1><span className="compat-h1-glyph" aria-hidden="true">{info.glyph}</span> {info.name} <em>compatibility</em></h1>

        {profile && (
          <section className="astro-card">
            <h2>How {info.name} loves</h2>
            {profile.in_love.map((paragraph) => <p key={paragraph.slice(0, 40)}>{paragraph}</p>)}
            <div className="compat-picks-row">
              <PickList title={`Best matches for ${info.name}`} items={profile.best} />
              <PickList title="Hardest matches" items={profile.hardest} />
            </div>
            <p className="astro-muted">{profile.element_note}</p>
          </section>
        )}

        <section className="astro-section">
          <h2>{info.name} with every sign</h2>
          <div className="compat-match-list">
            {ranked.map((match) => {
              const other = SIGN_BY_SLUG[match.other];
              return (
                <article key={match.other} className="compat-match" id={match.other}>
                  <header>
                    <h3>{info.glyph} {info.name} and {other.glyph} {other.name}</h3>
                    <ScoreBadge score={match.overall} />
                  </header>
                  <p className="compat-match-verdict">{match.verdict}. {match.headline}</p>
                  <p>{match.summary}</p>
                  <div className="compat-match-links">
                    <Link to={pairPath(sign, match.other)}>{info.name} man + {other.name} woman →</Link>
                    {match.other !== sign && (
                      <Link to={pairPath(match.other, sign)}>{other.name} man + {info.name} woman →</Link>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        </section>

        <section className="astro-card compat-share-card">
          <h2>Know a {info.name}?</h2>
          <p>Send them their matches, or send this to the {info.name} you’re curious about.</p>
          <ShareRow url={path} text={`${info.name} compatibility, every sign ranked:`} />
        </section>

        {profile?.faq && <Faq items={profile.faq} title={`${info.name} compatibility FAQ`} />}

        <section className="astro-section">
          <h2>Other signs</h2>
          <div className="compat-signs compact">
            {SIGNS.filter((item) => item.slug !== sign).map((item) => (
              <Link key={item.slug} to={`/zodiac-compatibility/${item.slug}/`} className="compat-sign-tile">
                <span className="astro-glyph" aria-hidden="true">{item.glyph}</span>
                <strong>{item.name}</strong>
              </Link>
            ))}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default CompatibilitySign;
